/**
 * Modal **somente leitura** que mostra a proposta completa do lead
 * (PR-M1-01c-iii.a+.b follow-up — pedido do Alexandre).
 *
 * Antes só tínhamos "Excluir" e "Enviar (em breve)" no card. Karla
 * precisa ler o corpo inteiro da proposta (notas geradas pela IA,
 * valores, parcelas de consultoria) pra decidir se aprova ou quer
 * editar antes de enviar.
 *
 * UX:
 *   - Modal centralizado `sm:max-w-2xl`
 *   - Seções: metadata (tipo, status, datas), Plataforma (se aplicável),
 *     Consultoria (se aplicável), Notas (monospace pra respeitar quebras)
 *   - Botão "Fechar" apenas — edição é em outro modal (EditProposal).
 */
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@kmgestao/ui';

import {
  CONSULTING_MODALITY_LABELS,
  CONSULTING_PAYMENT_METHOD_LABELS,
  PLAN_LABELS,
  PROPOSAL_STATUS_LABELS,
  PROPOSAL_TYPE_LABELS,
  type LeadProposalSummary,
} from '../../../features/proposals/index.js';

export interface ViewProposalModalProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  proposal: LeadProposalSummary;
  /** Callback "Editar" — quando fornecido, renderiza botão que fecha
   * este modal e aciona o editor (`NewProposalDialog` em modo edição).
   * Só relevante quando status='draft'. */
  onEdit?: (() => void) | undefined;
}

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR');
  } catch {
    return iso;
  }
}

/** Par label/valor formatado — visual consistente em grid 2 colunas. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{children}</dd>
    </div>
  );
}

export function ViewProposalModal({
  open,
  onOpenChange,
  proposal,
  onEdit,
}: ViewProposalModalProps) {
  // Editável em qualquer estado "pré-fechamento" — PR-M1-01c-ii.i (2026-04-24).
  // Karla pode reajustar mesmo depois de enviado/lido quando conversa
  // por WhatsApp ou outros canais fora do app.
  const canEdit =
    (proposal.status === 'draft' ||
      proposal.status === 'sent' ||
      proposal.status === 'viewed' ||
      proposal.status === 'in_negotiation') &&
    typeof onEdit === 'function';
  const hasSaas = proposal.proposal_type === 'saas' || proposal.proposal_type === 'saas_consulting';
  const hasConsulting =
    proposal.proposal_type === 'consulting' || proposal.proposal_type === 'saas_consulting';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Detalhes da proposta</DialogTitle>
          <DialogDescription>
            {PROPOSAL_TYPE_LABELS[proposal.proposal_type]} · Status:{' '}
            {PROPOSAL_STATUS_LABELS[proposal.status]}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Metadata */}
          <section>
            <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Informações gerais
            </h4>
            <dl className="grid gap-3 sm:grid-cols-2">
              <Field label="Criada em">{formatDateTime(proposal.created_at)}</Field>
              <Field label="Válida até">{formatDate(proposal.valid_until)}</Field>
              {proposal.sent_at ? (
                <Field label="Enviada em">{formatDateTime(proposal.sent_at)}</Field>
              ) : null}
              {proposal.viewed_at ? (
                <Field label="Visualizada em">{formatDateTime(proposal.viewed_at)}</Field>
              ) : null}
              {proposal.accepted_at ? (
                <Field label="Aceita em">{formatDateTime(proposal.accepted_at)}</Field>
              ) : null}
              {proposal.rejected_at ? (
                <Field label="Recusada em">{formatDateTime(proposal.rejected_at)}</Field>
              ) : null}
            </dl>
          </section>

          {/* SaaS */}
          {hasSaas ? (
            <section>
              <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Plataforma (SaaS)
              </h4>
              <dl className="grid gap-3 sm:grid-cols-2">
                <Field label="Plano">
                  {proposal.plan_name_pt_br ??
                    (proposal.plan_key
                      ? (PLAN_LABELS[proposal.plan_key] ?? proposal.plan_key)
                      : '—')}
                </Field>
                <Field label="Trial">
                  {proposal.trial_days === null
                    ? '—'
                    : `${proposal.trial_days} ${proposal.trial_days === 1 ? 'dia' : 'dias'}`}
                </Field>
                {proposal.custom_price_brl !== null ? (
                  <Field label="Preço customizado">
                    {BRL.format(proposal.custom_price_brl)}/mês
                  </Field>
                ) : proposal.discount_percent !== null ? (
                  <Field label="Desconto">{proposal.discount_percent}%</Field>
                ) : null}
                {proposal.proposal_type === 'saas_consulting' ? (
                  <Field label="SaaS durante consultoria">
                    {proposal.saas_free_during_consulting
                      ? 'Gratuito enquanto consultoria vigente'
                      : 'Cobrança normal desde o início'}
                  </Field>
                ) : null}
              </dl>
            </section>
          ) : null}

          {/* Consultoria */}
          {hasConsulting ? (
            <section>
              <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Consultoria
              </h4>
              <dl className="grid gap-3 sm:grid-cols-2">
                <Field label="Parcelas">
                  {proposal.consulting_installments_count !== null &&
                  proposal.consulting_installment_brl !== null
                    ? `${proposal.consulting_installments_count}× ${BRL.format(proposal.consulting_installment_brl)}`
                    : '—'}
                </Field>
                <Field label="Duração">
                  {proposal.consulting_duration_months !== null
                    ? `${proposal.consulting_duration_months} meses`
                    : '—'}
                </Field>
                <Field label="Horas semanais">
                  {proposal.consulting_weekly_hours !== null
                    ? `${proposal.consulting_weekly_hours}h/semana`
                    : '—'}
                </Field>
                <Field label="Valor hora extra">
                  {proposal.consulting_extra_hour_brl !== null
                    ? `${BRL.format(proposal.consulting_extra_hour_brl)}/h`
                    : '—'}
                </Field>
                <Field label="Modalidade">
                  {proposal.consulting_modality
                    ? CONSULTING_MODALITY_LABELS[proposal.consulting_modality]
                    : '—'}
                </Field>
                {/* Campo legado (PR-M1-01c-ii.d): só renderiza quando a proposta
                    foi criada antes da remoção do form — rascunhos novos sempre null. */}
                {proposal.consulting_payment_method ? (
                  <Field label="Pagamento (legado)">
                    {CONSULTING_PAYMENT_METHOD_LABELS[proposal.consulting_payment_method]}
                  </Field>
                ) : null}
                {proposal.consulting_scope_notes ? (
                  <div className="sm:col-span-2">
                    <dt className="text-xs font-medium text-muted-foreground">
                      Escopo customizado
                    </dt>
                    <dd className="mt-0.5 whitespace-pre-wrap text-sm text-foreground">
                      {proposal.consulting_scope_notes}
                    </dd>
                  </div>
                ) : null}
              </dl>
            </section>
          ) : null}

          {/* Notas */}
          <section>
            <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Corpo da proposta (notas)
            </h4>
            {proposal.notes ? (
              <div className="whitespace-pre-wrap rounded-md border border-border bg-muted/20 p-4 text-sm leading-relaxed text-foreground">
                {proposal.notes}
              </div>
            ) : (
              <p className="text-sm italic text-muted-foreground">Sem notas escritas ainda.</p>
            )}
          </section>

          {/* Mensagem de dúvida do lead (se houver) */}
          {proposal.questioning_message ? (
            <section className="rounded-md border border-warning/40 bg-warning/5 p-4">
              <h4 className="mb-2 text-sm font-semibold text-foreground">
                Lead deixou uma dúvida antes de aceitar
              </h4>
              <p className="whitespace-pre-wrap text-sm text-foreground">
                {proposal.questioning_message}
              </p>
            </section>
          ) : null}
        </div>

        <DialogFooter className="gap-2">
          <DialogClose asChild>
            <Button type="button" variant="outline" size="lg">
              Fechar
            </Button>
          </DialogClose>
          {canEdit ? (
            <Button
              type="button"
              size="lg"
              onClick={() => {
                onOpenChange(false);
                onEdit?.();
              }}
            >
              {proposal.status === 'draft' ? 'Editar rascunho' : 'Editar proposta'}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
